<template>
  <div v-if="isLoadingSenders || isLoadingCustomFields" class="d-flex justify-content-center align-items-center">
    <CSpinner class="my-3" color="primary" />
  </div>
  <div v-else>
    <div class="mb-4">
      <label for="Sender" class="font-weight-bold">
        {{ $t('automation.sms.content.select-1.value') }}
      </label>
      <multiselect
        class="no-close border-gray-300 rounded select"
        v-model="senderSelect"
        custom
        @input="selectSender()"
        :multiple="false"
        :allow-empty="false"
        :loading="isLoadingSenders"
        :disabled="isLoadingSenders"
        :custom-label="(option) => option.label"
        :options="senderSelectOptions"
        :placeholder="$t(`automation.sms.content.select.one`)"
      />
    </div>
    <div class="mb-4">
      <label for="CustomFields" class="d-flex justify-content-between">
        <span class="font-weight-bold">{{ $t('automation.sms.content.select-2.value') }}</span>
        <span style="font-size: 14px" class="text-gray-600">{{ $t('automation.sms.content.select-2.observation') }}</span>
      </label>
      <multiselect
        class="no-close border-gray-300 rounded select mb-3"
        v-model="customFieldSelect"
        custom
        :multiple="false"
        :loading="isLoadingCustomFields"
        :disabled="isLoadingCustomFields"
        :custom-label="(option) => option.value"
        :options="customFieldsSelectOptions"
        :allow-empty="false"
        :placeholder="$t(`automation.sms.content.select.one`)"
      />
      <CButton :disabled="!customFieldSelect" color="primary" class="px-4 py-2 table-header-button align-middle" @click="addCustomField()">
        {{ $t('automation.sms.content.select-2.action') }}
      </CButton>
    </div>
    <div class="mb-4">
      <label for="Message" class="d-flex justify-content-between">
        <span class="font-weight-bold">{{ $t('automation.sms.content.message.value') }}</span>
      </label>
      <textarea
        @keyup="changeText"
        ref="messageTextareaRef"
        class="form-textarea form-control"
        v-model="messageText"
        :maxlength="messageTextMaxAvailable"
      />
      <div class="d-flex">
        <div class="mr-4">
          <span class="d-block text-gray-600">{{ $t('automation.sms.content.message.indicator-1') }}</span>
          <span class="font-weight-bold">{{ messageTextLength }} / {{ messageTextMaxDynamicChars }}</span>
        </div>
        <div class="mr-4">
          <span class="d-block text-gray-600">{{ $t('automation.sms.content.message.indicator-2') }}</span>
          <span class="font-weight-bold">{{ messageTextAvailableChars }} / {{ messageTextMaxAvailable }}</span>
        </div>
        <div class="mr-4">
          <span class="d-block text-gray-600">{{ $t('automation.sms.content.message.indicator-3') }}</span>
          <span class="font-weight-bold">{{ messageTextPerContact }}</span>
        </div>
        <div class="">
          <span class="d-block text-gray-600">{{ $t('automation.sms.content.message.indicator-4') }}</span>
          <span class="font-weight-bold">{{ messageTextUnicodeType }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { FIELD_TYPE } from '../constants'

export default {
  name: 'AutomationSMSContentNew',
  props: {
    senderSelectOptions: {
      type: Array,
      default: () => [],
    },
    isLoadingSenders: {
      type: Boolean,
      default: false,
    },
    customFieldsSelectOptions: {
      type: Array,
      default: () => [],
    },
    isLoadingCustomFields: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      senderSelect: undefined,
      customFieldSelect: undefined,
      messageText: undefined,
      messageTextCharsIncrementingValue: 160,
      messageTextPerContact: 1,
      messageTextUnicodeType: 'GSM 7 - bit',
      textTimeout: null,
    }
  },
  methods: {
    loadFields() {
      let contentSMS = this.$store.state.automation.mainWorkflow.workflowJson.steps.values[0].data
      this.messageText = contentSMS.text
      this.senderSelect = { value: contentSMS.senderId, label: contentSMS.senderName }
    },
    addCustomField() {
      this.$refs.messageTextareaRef.focus()

      const messageTextLength = this.messageText?.length || 0
      const personalizationTagLength = this.customFieldSelect.personalizationTag.length
      const willExceedMax = messageTextLength + personalizationTagLength > Number(this.messageTextMaxAvailable)

      if (willExceedMax) return

      if (!this.messageText) {
        this.messageText = '[' + this.customFieldSelect.personalizationTag + ']'
      } else {
        this.messageText += '[' + this.customFieldSelect.personalizationTag + ']'
      }

      this.changeText()
    },

    selectSender() {
      let senderId = {
        property: 'senderId',
        value: this.senderSelect.value,
        type: FIELD_TYPE.STEP,
      }
      this.$emit('update-field', senderId)

      let senderName = {
        property: 'senderName',
        value: this.senderSelect.label,
        type: FIELD_TYPE.STEP,
      }
      this.$emit('update-field', senderName)
    },

    changeText() {
      if (this.textTimeout) {
        clearTimeout(this.textTimeout)
      }

      this.textTimeout = setTimeout(() => {
        let obj = {
          property: 'text',
          value: this.messageText,
          type: FIELD_TYPE.STEP,
        }

        this.$emit('update-field', obj)
      }, 1000)
    },
  },
  computed: {
    messageTextLength() {
      return this.messageText?.length || 0
    },
    messageTextMaxDynamicChars() {
      return this.messageTextCharsIncrementingValue * this.messageTextPerContact
    },
    messageTextMaxAvailable() {
      return this.messageTextUnicodeType == 'GSM 7 - bit' ? '612' : '536'
    },
    messageTextAvailableChars() {
      return this.messageTextMaxAvailable - this.messageTextLength
    },
  },
  watch: {
    messageText() {
      var charset7bit = [
        '@',
        '£',
        '$',
        '¥',
        'è',
        'é',
        'ù',
        'ì',
        'ò',
        'Ç',
        '\n',
        'Ø',
        'ø',
        '\r',
        'Å',
        'å',
        'Δ',
        '_',
        'Φ',
        'Γ',
        'Λ',
        'Ω',
        'Π',
        'Ψ',
        'Σ',
        'Θ',
        'Ξ',
        'Æ',
        'æ',
        'ß',
        'É',
        ' ',
        '!',
        '"',
        '#',
        '¤',
        '%',
        '&',
        "'",
        '(',
        ')',
        '*',
        '+',
        ',',
        '-',
        '.',
        '/',
        '0',
        '1',
        '2',
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        ':',
        ';',
        '<',
        '=',
        '>',
        '?',
        '¡',
        'A',
        'B',
        'C',
        'D',
        'E',
        'F',
        'G',
        'H',
        'I',
        'J',
        'K',
        'L',
        'M',
        'N',
        'O',
        'P',
        'Q',
        'R',
        'S',
        'T',
        'U',
        'V',
        'W',
        'X',
        'Y',
        'Z',
        'Ä',
        'Ö',
        'Ñ',
        'Ü',
        '§',
        '¿',
        'a',
        'b',
        'c',
        'd',
        'e',
        'f',
        'g',
        'h',
        'i',
        'j',
        'k',
        'l',
        'm',
        'n',
        'o',
        'p',
        'q',
        'r',
        's',
        't',
        'u',
        'v',
        'w',
        'x',
        'y',
        'z',
        'ä',
        'ö',
        'ñ',
        'ü',
        'à',
      ]
      var charset7bitext = ['\f', '^', '{', '}', '\\', '[', '~', ']', '|', '€']
      var charsArr = this.messageText?.split('')
      var charsUsed = 0
      var part = 1
      var parts = 1
      var i = 0

      for (i = 0; i < charsArr?.length; i += 1) {
        if (charset7bit.indexOf(charsArr[i]) >= 0) {
          charsUsed += 1
          this.messageTextUnicodeType = 'GSM 7 - bit'
        } else if (charset7bitext.indexOf(charsArr[i]) >= 0) {
          charsUsed += 2
          this.messageTextUnicodeType = 'GSM 7 - bit'
        } else {
          this.messageTextUnicodeType = 'UCS2 - 16 bit'
          charsUsed = charsArr?.length
          break
        }
      }

      if (this.messageTextUnicodeType == 'GSM 7 - bit') {
        if (charsUsed > 160) {
          var parts = Math.ceil(charsUsed / 153)
          var parts_chars_used = 7
          this.messageTextCharsIncrementingValue = 153

          for (i = 0; i < charsArr?.length; i++) {
            if (part_chars_used + 1 > 160) {
              part = part + 1
              part_chars_used = 7
            }
            if (charset7bitext.indexOf(charsArr[i]) >= 0 && part_chars_used + 2 > 160) {
              part = part + 1
              part_chars_used = 7
            }
            if (charset7bitext.indexOf(charsArr[i]) == -1) {
              part_chars_used = part_chars_used + 1
            } else {
              part_chars_used = part_chars_used + 2
            }
          }
        } else {
          this.messageTextCharsIncrementingValue = 160
        }
      } else {
        if (charsUsed > 70) {
          var parts = Math.ceil(charsUsed / 67)
          var part_chars_used = 3
          this.messageTextCharsIncrementingValue = 67

          for (i = 0; i < charsArr?.length; i++) {
            if (part_chars_used + 1 > 70) {
              part = part + 1
              part_chars_used = 3
            }
            part_chars_used = part_chars_used + 1
          }
        } else {
          this.messageTextCharsIncrementingValue = 70
        }
      }

      if (charsUsed === 0) {
        this.messageTextPerContact = 1
        this.messageTextCharsIncrementingValue = 160
        this.messageTextUnicodeType = 'GSM 7 - bit'
      }

      this.charsUsed = charsUsed
      this.messageTextPerContact = parts
    },
  },
}
</script>

<style lang="scss" scoped>
.form-textarea {
  min-height: 222px;
}
</style>
